/**
 * NPC resolve chain (E2.4.S3) — turn a join-only `NpcWire` into the asset-pack +
 * model the placer instantiates at the NPC's (x, z). NPCs arrive in
 * `helloAck.npcs` (static, join-only — `messages.ts` `HelloAckPayload.npcs`; "the
 * current slice client ignores these; a future client renders + targets them");
 * the wire carries the CONTENT id, so this resolves by a DIRECT id lookup into the
 * client-pack `npcs` projection (no name fragility, unlike the enemy path).
 *
 *   NpcWire.id
 *     → client-pack npcs[id]                       (BASE_FIELDS incl. optional `presentation`)
 *       → NpcDef.presentation IF a usable binding  (assetPack + model, pack delivered) → source 'presentation'
 *       → else the S1 humanoid rig pack constant    (RIG_PACK_ID / RIG_BODY_MODEL) → source 'rig'
 *         (rendered with DEFAULT_APPEARANCE — NPCs carry no `CharacterAppearance`;
 *          they are not characters, and faction livery is an NPC-spec concern, not here)
 *       → else null ⇒ render NOTHING               (the genuine pack-absence state; no
 *          primitive placeholder — unlike players/enemies, NPCs have no existing
 *          visual to preserve, so inventing one would CHANGE the baseline)
 *
 * `0 of 13 committed NpcDefs carry `presentation`` today, so every NPC resolves via
 * the rig fallback (the interim `player-rig-v1`, AD-181 — committed + live per the
 * owner-directed E2.4.S2 activation). The presentation branch is a future-proof read
 * of the EXISTING optional field — NpcDef `presentation` content-binding stays
 * DEFERRED until a real NPC pack (`npc-base`) lands (CHR-026 §7; binding to a
 * nonexistent pack = a validator reference reject, never done here).
 *
 * FRONTEND-LOCAL by design: the presentation-first-then-rig resolution order is a
 * client convention (the AD-141 `RESOURCE_NODE_ASSET` / E2.2.S3 gather-tool-map /
 * E2.3 creature-name-map precedent) — never a `shared/` constant. (`presentation.model`
 * pack-membership IS a validator rule since AD-413; the resolution ORDER here is not —
 * an undelivered pack still degrades to no-render at the loader.) Promoting any of it is a T3 DR escalation out of the
 * feature. Pure + node-safe (types only from the client surface). Import boundary
 * (AD-122): shared types via `@great-cloud/shared/client`.
 */

import type { AssetPackDef, PresentationRef } from '@great-cloud/shared/client';

import { RIG_BODY_MODEL, resolveRig, type RigContent } from '../rig-avatar/index.js';

/**
 * The projected NPC fields the placer reads (client-pack `npcs`; the projection
 * ships BASE_FIELDS = id/name/description/`presentation` + roles/faction/biome/
 * dialogue — `client-pack.ts`). Only `presentation` drives resolution; id/name/x/z
 * come from the `NpcWire`, not the pack.
 */
export interface NpcPresentation {
  /** The EXISTING optional presentation binding (assetPack + model), or undefined. */
  presentation?: PresentationRef;
  /** The NPC's faction id (client-pack BASE projection) — picks its livery (livery.ts). */
  faction?: string;
  /** The NPC's roles (client-pack projection) — a questgiver gets a marker. */
  roles?: readonly string[];
  /** The NPC's ambient animation set (client-pack projection; ambient.ts). Absent ⇒ role default. */
  ambient?: string;
}

/**
 * How the placer reads NPC presentation data. EXTENDS `RigContent` (the client-pack
 * `asset-packs` + `appearance-options` projections) so the rig fallback reuses the
 * S1 `RigAvatarBuilder` unchanged, and ADDS the `npc(id)` lookup for the
 * presentation-first read. Built in App from the loaded `ContentPackStore`
 * (`get('npcs', id)` / `get('asset-packs', id)` / `get('appearance-options', id)`);
 * a small test fake satisfies it too. Kept a superset of `RigContent` so the feature
 * touches no other module's contract.
 */
export interface NpcContent extends RigContent {
  /** Client-pack `npcs[id]` (its optional `presentation`), or undefined if unknown. */
  npc(id: string): NpcPresentation | undefined;
}

/** Which branch produced a resolution — recorded for the tests + the S5 reviewer. */
export type NpcSource = 'presentation' | 'rig';

/** A resolved NPC render: the pack + model the placer instantiates, and the branch. */
export interface NpcResolution {
  /** The resolved asset-pack id (`presentation.assetPack` or `RIG_PACK_ID`). */
  assetPack: string;
  /** The asset id within the pack to instantiate at lod0 (`presentation.model` or `body-base`). */
  model: string;
  /** The resolved pack def — an `AssetManifest`/`AssetLoader` is built from it. */
  pack: AssetPackDef;
  /** 'presentation' = the def's own binding; 'rig' = the humanoid rig fallback. */
  source: NpcSource;
}

/** A usable presentation binding = both fields present AND the pack delivered. */
function resolvePresentation(
  content: Pick<NpcContent, 'npc' | 'assetPack'>,
  npcId: string,
): NpcResolution | null {
  const presentation = content.npc(npcId)?.presentation;
  if (presentation === undefined) return null;
  const { assetPack, model } = presentation;
  if (assetPack === undefined || assetPack === '' || model === undefined || model === '') {
    return null; // present but not a usable binding ⇒ fall through to the rig
  }
  const pack = content.assetPack(assetPack);
  if (pack === undefined) return null; // binds an undelivered pack ⇒ fall through to the rig
  return { assetPack, model, pack, source: 'presentation' };
}

/**
 * Resolve one NPC's render, or `null` when nothing can be rendered (no def
 * presentation AND no rig pack delivered) — the placer then adds NOTHING to the
 * scene (the genuine pack-absence baseline, no placeholder). The final
 * `instantiate(model, lod0)` — and its own null fallback for an unknown model /
 * load error — is the placer's/loader's job.
 */
export function resolveNpc(content: NpcContent, npcId: string): NpcResolution | null {
  // 1. Presentation-first: use the def's own binding when it is a usable one.
  const bound = resolvePresentation(content, npcId);
  if (bound !== null) return bound;

  // 2. Humanoid rig fallback: the S1 rig pack constant + `body-base`, default look.
  const rig = resolveRig(content);
  if (rig !== null) {
    return { assetPack: rig.packId, model: RIG_BODY_MODEL, pack: rig.pack, source: 'rig' };
  }

  // 3. Neither ⇒ render nothing (no primitive placeholder — today's baseline).
  return null;
}
